import * as React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import Divider from '@mui/material/Divider';
import qs from 'query-string';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import { Button } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';
import {
  Collector,
  CollectorInstallError,
  CollectorScope,
  CollectorSettingScope,
  CollectorType,
  Maintainer
} from 'graphql/types';
import { useMessages } from 'modules/intl/LocaleIntlProvider';
import { useMeContext } from 'modules/me';
import { formatDateFromIsoString } from 'utils/date-time';
import { useFeatureFlag } from 'modules/common/hooks';
import { collectorInstall, isWithinAnotherApp } from '../../containerApp/index';
import { CollectorCategory } from './utils';
import { ErrorTitle } from './constants';
import { CollectorIconType, infoType } from './AllCollectorsListPage';
import { MenuPopper } from './MenuPopper';
import { useHandleDisconnect } from './Setting/hooks/useOnDisconnect';
import { useDisconnectDisabled } from './hooks/useDisconnectDisabled';

const useStyles = makeStyles((theme) => ({
  infoView: {
    width: theme.spacing(25)
  },
  imageContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5, 1.5, 1.5, 1.5),
    flexDirection: 'column'
  },

  collectorIcon: {
    alignSelf: 'center',
    display: 'flex',
    height: theme.spacing(15),
    width: theme.spacing(15),
    borderRadius: theme.spacing(3),
    padding: theme.spacing(2),
    border: `solid 3px ${theme.palette.grey[200]}`
  },
  installButton: {
    width: theme.spacing(15),
    borderRadius: theme.spacing(1)
  },
  bottomnDivider: {
    borderBottomWidth: theme.spacing(0.25),
    width: theme.spacing(25.5),
    color: theme.palette.grey[200]
  },
  connected: {
    display: 'flex',
    margin: theme.spacing(-1.5, 0, 0, 0),
    ...theme.typography.caption,
    fontSize: theme.spacing(1.5),
    color: theme.palette.common.white,
    backgroundColor: theme.palette.success.dark,
    width: 'fit-content',
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0, 0.8)
  },
  imageConnectedDiv: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    marginBottom: theme.spacing(1.5)
  },
  textDiv: {
    margin: theme.spacing(0, 1, 2, 4),
    color: theme.palette.text.primary
  },
  textInline: {
    display: 'flex',
    flexDirection: 'row'
  },
  availability: {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.body2.fontSize
  },
  scope: {
    fontSize: theme.typography.body2.fontSize
  },
  nextline: {
    whiteSpace: 'pre'
  },
  textdivInstallInfo: {
    margin: theme.spacing(1.5, 1, 2, 4),
    color: theme.palette.text.primary,
    fontSize: theme.typography.caption.fontSize,
    display: 'flex',
    flexDirection: 'column'
  },
  marginBottom: {
    marginBottom: theme.spacing(1.5)
  },
  span: {
    display: 'flex',
    flexDirection: 'row'
  },
  textIdColor: {
    color: theme.palette.text.secondary
  },
  textNameColor: {
    color: theme.palette.text.primary,
    marginLeft: theme.spacing(0.5)
  }
}));

const typeIconStyle = makeStyles((theme) => ({
  icon: {
    alignSelf: 'center !important',
    color: theme.palette.grey[500],
    marginBottom: theme.spacing(2)
  }
}));

type CollectorDetailSidePanelProps = {
  info: infoType | null;
  handleClose: () => void;
  handleDialogOpen: () => void;
  installationScopeSet: Set<string>;
  scopeSet: Set<string>;
  installInformation: {
    userMaintainers?: Maintainer[];
    userInstallDate?: string;
    tenantMaintainers?: Maintainer[];
    tenantInstallationDate?: string;
  };
};

const messageKeys = [
  'collectors.configure',
  'collectors.uninstall',
  'collectorSetting.install',
  'collectorSetting.disconnectSmall',
  'collectors.installCopy'
];
export const CollectorDetailSidePanel: React.FC<CollectorDetailSidePanelProps> = ({
  info,
  handleClose,
  handleDialogOpen,
  scopeSet,
  installationScopeSet,
  installInformation
}: CollectorDetailSidePanelProps) => {
  const collectorInstallErrors = info?.tenantInstallStatus?.errors;
  const menuPopperMessages = useMessages(messageKeys);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [
    mouseOverButtonType,
    setMouseOverButtonType
  ] = useState<CollectorScope>('' as CollectorScope);
  const menuAnchorTenantE1 = useRef<HTMLLIElement>(null);
  const menuAnchorUserE1 = useRef<HTMLLIElement>(null);
  const { isDisconnectDisabled } = useDisconnectDisabled({
    collectorId: info?.id,
    collectorType: info?.type
  });
  const [openTenant, setOpenTenant] = useState(false);
  const [openUser, setOpenUser] = useState(false);

  const isPluginOrDesktop = useMemo(
    () =>
      info?.type === CollectorType.Plugin ||
      info?.type === CollectorType.Desktop,
    [info?.type]
  );

  const isBrowserWhitelistEnabled: boolean = useFeatureFlag(
    'isBrowserWhitelistEnabled'
  );
  const me = useMeContext();
  const isAdmin = useMemo(() => {
    return me?.permissions.hasCompanyCollectorInstallAccess ? true : false;
  }, [me]);

  const showConfigure = useMemo(() => {
    if (isAdmin && (info?.configURL as string)?.length > 0) {
      return (
        installationScopeSet.has(CollectorSettingScope.Tenant) ||
        info?.type === CollectorType.Desktop ||
        (isBrowserWhitelistEnabled &&
          info?.type === CollectorType.Plugin &&
          info?.category === CollectorCategory.Browser)
      );
    }
    return false;
  }, [
    info?.category,
    info?.configURL,
    info?.type,
    installationScopeSet,
    isBrowserWhitelistEnabled,
    isAdmin
  ]);

  const showInstall = useMemo(() => info?.type !== CollectorType.Desktop, [
    info?.type
  ]);

  const handleConnectClick = useCallback(
    (scope) => {
      if (!isPluginOrDesktop) {
        const query = qs.stringify({
          id: info?.id,
          scope: scope
        });
        const { origin } = window.location;
        const url = `${origin}/collector/install?${query}`;
        if (isWithinAnotherApp()) {
          collectorInstall(url);
        } else {
          window.open(`/collector/install?${query}`);
        }
      } else {
        window.open(
          scope === CollectorScope.Tenant
            ? (info?.installURL as string)
            : (info?.userInstallURL as string)
        );
      }
      handleClose();
    },
    [
      handleClose,
      info?.id,
      info?.installURL,
      info?.userInstallURL,
      isPluginOrDesktop
    ]
  );
  const handleConfigurePage = useCallback(() => {
    window.open(info?.configURL as string);
    handleClose();
  }, [handleClose, info]);

  const onMouseOverCompany = useCallback(() => {
    if (installationScopeSet.size > 0 || showConfigure) {
      setOpenTenant(true);
      setMouseOverButtonType(CollectorScope.Tenant);
    }
  }, [installationScopeSet, showConfigure]);

  const onMouseOverUser = useCallback(() => {
    if (installationScopeSet?.size > 0) {
      setOpenUser(true);
      setMouseOverButtonType(CollectorScope.User);
    }
  }, [installationScopeSet?.size]);

  const onMouseLeave = useCallback(() => {
    setOpenTenant(false);
    setMouseOverButtonType('' as CollectorScope);
  }, []);

  const onMouseLeaveUser = useCallback(() => {
    setOpenUser(false);
    setMouseOverButtonType('' as CollectorScope);
  }, []);
  const onDisconnect = useHandleDisconnect({
    onClose: handleClose,
    scope: mouseOverButtonType
  });

  const isConfigureDisabled = useMemo(
    () =>
      Boolean(
        collectorInstallErrors?.find(
          (err) =>
            (err as CollectorInstallError).title ===
            ErrorTitle.NOTIFICATION_401_403
        )
      ),
    [collectorInstallErrors]
  );

  const menuItem = useMemo(() => {
    if (mouseOverButtonType === CollectorScope.Tenant) {
      const companyComponents = [];
      if (showConfigure) {
        companyComponents.push({
          name: menuPopperMessages['collectors.configure'],
          onClick: () => handleConfigurePage(),
          testId: `configure-button-${info?.name}`,
          disabled: isConfigureDisabled,
          toolTip: isConfigureDisabled ? (
            <FormattedMessage id="collectorError.configureDisabledMessage" />
          ) : (
            ''
          )
        });
      }
      if (installationScopeSet.has(CollectorSettingScope.Tenant)) {
        companyComponents.push({
          name: menuPopperMessages['collectors.uninstall'],
          onClick: onDisconnect(info as Collector),
          testId: `uninstall-button-${info?.name}`
        });
      } else {
        if (showInstall) {
          companyComponents.push({
            name: menuPopperMessages['collectorSetting.install'],
            onClick: () => handleConnectClick(CollectorSettingScope.Tenant),
            testId: `install-button-${info?.name}`
          });
        }
      }
      return companyComponents;
    } else if (mouseOverButtonType === CollectorScope.User) {
      const userArray: {
        name: string;
        onClick: () => void;
        testId: string;
        disabled?: boolean;
      }[] = [];
      if (installationScopeSet.has(CollectorSettingScope.User)) {
        if (isPluginOrDesktop) {
          userArray.push({
            name: menuPopperMessages['collectors.installCopy'],
            onClick: () => handleConnectClick(CollectorSettingScope.User),
            testId: `install-user-copy-button-${info?.name}`
          });
        }
        userArray.push({
          name: isPluginOrDesktop
            ? menuPopperMessages['collectorSetting.disconnectSmall']
            : menuPopperMessages['collectors.uninstall'],
          onClick: isPluginOrDesktop
            ? handleDialogOpen
            : onDisconnect(info as Collector),
          testId: `uninstall-button-user-${info?.name}`,
          disabled: isDisconnectDisabled
        });

        return userArray;
      } else {
        return [
          {
            name: menuPopperMessages['collectorSetting.install'],
            onClick: () => handleConnectClick(CollectorSettingScope.User),
            testId: `install-button-${info?.name}`
          }
        ];
      }
    }
    return [];
  }, [
    handleConnectClick,
    handleDialogOpen,
    handleConfigurePage,
    info,
    isConfigureDisabled,
    installationScopeSet,
    isPluginOrDesktop,
    menuPopperMessages,
    mouseOverButtonType,
    onDisconnect,
    showConfigure,
    showInstall,
    isDisconnectDisabled
  ]);

  const handleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleButtonClose = () => {
    setAnchorEl(null);
  };
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const altText = formatMessage(
    {
      id: 'collectors.imageAltText'
    },
    { collectorName: info?.name }
  );

  const installationComponent = useCallback(
    (userId, displayText, date, index) => {
      return (
        <>
          <Typography
            data-testid={'installedByNameText'}
            variant="body2"
            key={`${userId}-${index}`}
          >
            {displayText}
          </Typography>

          <span className={classes.span}>
            <Typography
              variant="body2"
              className={classes.textIdColor}
              data-testid="installedOnText"
            >
              <FormattedMessage id="installedCollectors.installedOn" />
              &nbsp;
            </Typography>
            <Typography data-testid="installedOnDate" variant="body2">
              {formatDateFromIsoString(date || '', me)}
            </Typography>
          </span>
          <br role="presentation" />
        </>
      );
    },
    [classes.span, classes.textIdColor, me]
  );

  const showInstallInformation = useMemo(
    () =>
      (installInformation?.tenantMaintainers || [])?.length > 0 ||
      (installInformation?.userMaintainers || [])?.length > 0,
    [installInformation?.tenantMaintainers, installInformation?.userMaintainers]
  );

  const installationDetails = useMemo(() => {
    const userDetails: JSX.Element[] = [];
    const tenantDetails: JSX.Element[] = [];

    installInformation.userMaintainers?.forEach((i, index) => {
      userDetails.push(
        installationComponent(
          i.userId,
          i.displayText,
          installInformation.userInstallDate,
          index
        )
      );
    });
    installInformation.tenantMaintainers?.map((i, index) => {
      tenantDetails.push(
        installationComponent(
          i.userId,
          i.displayText,
          installInformation.tenantInstallationDate,
          index
        )
      );
    });
    return {
      installDetails: [...userDetails, ...tenantDetails]
    };
  }, [installInformation, installationComponent]);
  return (
    <div className={classes.infoView}>
      <div className={classes.imageContainer}>
        <div className={classes.imageConnectedDiv}>
          <img
            className={classes.collectorIcon}
            src={info?.image.src}
            alt={altText}
            data-testid="collectorImgIcon"
          />
          {installationScopeSet?.size > 0 && (
            <Typography
              className={classes.connected}
              variant="caption"
              data-testid={'connected-button-' + info?.name}
            >
              <FormattedMessage id="collectors.installed" />
            </Typography>
          )}
        </div>
        <Button
          variant="contained"
          className={classes.installButton}
          data-testid="collectorDetailButton"
          onClick={handleButtonClick}
        >
          {installationScopeSet.size > 0 ? (
            <FormattedMessage id="collectors.modify" />
          ) : (
            <FormattedMessage id="collectors.install" />
          )}
        </Button>

        <MenuPopper
          menuItem={menuItem}
          anchorPopper={menuAnchorTenantE1}
          menuAnchorUserE1={menuAnchorUserE1}
          anchorMenu={anchorEl}
          handleButtonClose={handleButtonClose}
          onMouseOverCompany={onMouseOverCompany}
          onMouseOverUser={onMouseOverUser}
          onMouseLeave={onMouseLeave}
          onMouseLeaveUser={onMouseLeaveUser}
          scopeSet={scopeSet}
          installationScopeSet={installationScopeSet}
          connectClick={handleConnectClick}
          openTenant={openTenant}
          openUser={openUser}
          showConfigure={showConfigure}
        />
        <CollectorIconType
          type={info?.type || undefined}
          overrideClass={typeIconStyle()}
        />
        <Divider
          orientation="horizontal"
          className={classes.bottomnDivider}
          data-testid="collectorImgDivider"
        ></Divider>
      </div>
      <div className={classes.textDiv}>
        <div className={classes.textInline} data-testid="collectorPublishedBy">
          <Typography variant="body2" className={classes.textIdColor}>
            <FormattedMessage id="collectors.by" />
            <span className={classes.textNameColor}>{info?.publishedBy}</span>
          </Typography>
        </div>
        <div className={classes.textInline} data-testid="collectorCategoryText">
          <Typography variant="body2" className={classes.textIdColor}>
            <FormattedMessage id="collectors.category" />
            <span className={classes.textNameColor}>{info?.category}</span>
          </Typography>
        </div>
        <br role="presentation" />
        <div>
          <Typography
            className={classes.availability}
            data-testid="collectorAvailabilityText"
            variant="body2"
          >
            <FormattedMessage id="collectors.availability" />
          </Typography>
          {(info?.appScope || []).map((i) => (
            <Typography
              key={i}
              className={classNames(classes.scope, classes.nextline)}
              data-testid={`collectorAvailabilityContent${i}`}
              variant="body2"
            >
              <FormattedMessage id={`collectors.appScope.${i}`} />
            </Typography>
          ))}
        </div>
      </div>
      <Divider
        orientation="horizontal"
        className={classNames(classes.bottomnDivider, classes.marginBottom)}
        data-testid="collectorImgDividerInstall"
      ></Divider>
      {showInstallInformation && (
        <>
          <div className={classes.textdivInstallInfo}>
            <Typography
              variant="body2"
              className={classes.textIdColor}
              data-testid="collectorInstalledByText"
            >
              <FormattedMessage id="installedCollectors.installedBy" />
            </Typography>
            {installationDetails.installDetails}
          </div>
        </>
      )}
    </div>
  );
};
